// Imports
import { Font } from "fajny-react"

const createOverride = (
    <>
        You can change this by creating a new <code>css</code> file and
        importing it in your <code>index.js</code>.
    </>
)

const allComponentsPages = [
    // Styles
    {
        title: "colors",
        intro: (
            <Font.P>
                Fajny CSS has its own color palette. If you want to use your own colors, you can create a{" "}
                <code>css</code> file and import it in your <code>index.js</code>. You can find a nice color palette
                generator here:{" "}
                <a href="https://coolors.co/" target="_blank" rel="noreferrer noopener">
                    https://coolors.co/
                </a>
                .
            </Font.P>
        ),
        include: "Variables",
    },
    {
        title: "fonts",
        intro: <Font.P>By default, Fajny CSS uses Lato for the font. {createOverride}</Font.P>,
        include: "Font",
    },
    {
        title: "shadows",
        intro: <Font.P>Fajny CSS uses different shadows for some components. {createOverride}</Font.P>,
        include: "Variables",
    },
    {
        title: "radiuses",
        intro: <Font.P>All the different radiuses we're using. {createOverride}</Font.P>,
        include: "Variables",
    },

    // Layouts
    {
        title: "container",
        intro: (
            <Font.P>
                Containers are required for the main part of your website. Three sizes are available: 600px (default),
                400px for forms and 800px for large pages. It is also possible to add an <code>&lt;aside&gt;</code>.
            </Font.P>
        ),
        include: "Wrapper, Main, Aside",
    },
    {
        title: "section",
        intro: "Each blocks of your website can be contained in a section.",
        include: "Section",
    },
    {
        title: "spacers",
        intro: "Fajny CSS have different spacers, based on an 8px grid.",
        include: "Variables",
    },
    {
        title: "grid",
        intro: "Here we're using CSS grid. By default, there is only one column and no gap between elements.",
        include: "Grid",
    },
    {
        title: "flexbox",
        intro: "Component using CSS flexbox.",
        include: "Flexbox",
    },

    // Components
    {
        title: "icon",
        intro: "Component to use with your own icons.",
        include: "Icon",
    },
    {
        title: "hr",
        intro: (
            <Font.P>
                Use <code>hr</code> component to separate your content.
            </Font.P>
        ),
        include: "Hr",
    },
    {
        title: "image",
        intro: (
            <Font.P>
                Container for images. Here, we are using{" "}
                <a
                    href="https://reactjs.org/docs/react-api.html#reactsuspense"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    React Suspense
                </a>{" "}
                for lazy loading.
            </Font.P>
        ),
        include: "Image",
    },
    {
        title: "video",
        intro: (
            <Font.P>
                Container for HTML videos. Be careful, the HTML 5 autoplay function only works with the sound off. Here,
                we are using{" "}
                <a
                    href="https://reactjs.org/docs/react-api.html#reactsuspense"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    React Suspense
                </a>{" "}
                for lazy loading.
            </Font.P>
        ),
        include: "Video",
    },
    {
        title: "youtube",
        intro: "Iframe container for YouTube videos. For every video, the YouTube url is enough, and will be converted to an embed one.",
        include: "Youtube",
    },
    {
        title: "avatar",
        intro: "Avatars are created in three different versions: with picture, with a letter and with an icon.",
        include: "Avatar",
    },
    {
        title: "loader",
        intro: "Notify your user that something is loading.",
        include: "Loader",
    },
    {
        title: "button",
        intro: (
            <Font.P>
                Buttons exist in different styles and colors. You can also use this component as a{" "}
                <code>&lt;Link&gt;</code>.
            </Font.P>
        ),
        include: "Button",
    },
    {
        title: "button-icon",
        intro: (
            <Font.P>
                Buttons with icon exist in different styles and colors. You can also use this component as a{" "}
                <code>&lt;Link&gt;</code>.
            </Font.P>
        ),
        include: "ButtonIcon",
    },
    {
        title: "tag",
        intro: "Tags can be used to display information.",
        include: "Tag",
    },
    {
        title: "badge",
        intro: "Badges can be used to notify users about important informations: new items, unread messages, etc.",
        include: "Badge",
    },
    {
        title: "tooltip",
        intro: "Use tooltip to display more information to a user.",
        include: "Tooltip",
    },
    {
        title: "input",
        intro: "All the HTML inputs but checkboxes or radio. For a better user experience, try to always have a label.",
        include: "Input",
    },
    {
        title: "input-image",
        intro: "Input showing an uploaded picture automatically.",
        include: "InputImage",
    },
    {
        title: "input-check",
        intro: "Radio, checkboxes and different styles for them.",
        include: "InputCheck",
    },
    {
        title: "form",
        intro: (
            <Font.P>
                For every form page, try to have the <code>size</code> of your <code>Main</code> element set to{" "}
                <code>form</code>.
            </Font.P>
        ),
        include: "Form",
    },
    {
        title: "breadcrumbs",
        intro: "Breadcrumbs help users locate the page they are in.",
        include: "Breadcrumbs",
    },
    {
        title: "accordion",
        intro: "Use accordion to display hidden information to users.",
        include: "Accordion",
    },
    {
        title: "dropdown",
        intro: (
            <Font.P>
                Dropdowns can either be aligned on the left, or the right, depending on your needs. Any{" "}
                <code>button</code> can be used to open the dropdown, but always remember to add <code>onClick</code>{" "}
                function.
            </Font.P>
        ),
        include: "DropdownContainer, Dropdown",
    },
    {
        title: "toast",
        intro: "Toast are used to notify the user.",
        include: "Toast",
    },
    {
        title: "alert",
        intro: "Alerts can be used to notify users on a page.",
        include: "Alert",
    },
    {
        title: "modal",
        intro: "Use modals to show something full screen to the user.",
        include: "Modal",
    },
    {
        title: "pagination",
        intro: "Responsive pagination for your list pages. It can be useful not to load all your articles on the same page when using it in a blog.",
        include: "Pagination, PaginationButton",
    },
    {
        title: "paginator",
        intro: (
            <Font.P>
                Use it instead of <code>Pagination</code> when you have at least 50 pages.
            </Font.P>
        ),
        include: "Paginator",
    },
    {
        title: "stepper",
        intro: (
            <Font.P>
                Use the <code>Stepper</code> component to navigate inside a multi pages form for example.
            </Font.P>
        ),
        include: "Stepper",
    },
    {
        title: "progress-bar",
        intro: "Progress bars are a nice way to display informations. They exist in different colors, with or without animation. Values go from 0 to 100.",
        include: "ProgressBar",
    },
    {
        title: "tabs",
        intro: "Add tabs to display only certain informations.",
        include: "Tabs",
    },
    {
        title: "slideshow",
        intro: "Slideshows are a nice way to show images or changing content.",
        include: "Slideshow",
    },
    {
        title: "burger",
        intro: "Button mainly used for navigation on mobile.",
        include: "Burger",
    },
    {
        title: "card-basic",
        intro: "Cards are a nice way to display content.",
        include: "CardBasic",
    },
    {
        title: "card-image",
        intro: "Cards with image can be used mostly for links.",
        include: "CardImage",
    },
    {
        title: "card-action",
        intro: "Action cards can display a few information.",
        include: "CardAction",
    },
    {
        title: "cover",
        intro: "A parallax cover can be a nice way to start a page.",
        include: "Cover",
    },
    {
        title: "table",
        intro: "Tables can be a good way to display informations to the user.",
        include: "Table",
    },
    {
        title: "page-loading",
        intro: "Show to the user that your content is loading.",
        include: "PageLoading",
    },
    {
        title: "code-container",
        intro: (
            <Font.P>
                Display some code to the user. For better experience, add{" "}
                <a
                    href="https://github.com/react-syntax-highlighter/react-syntax-highlighter"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    react-syntax-highlighter
                </a>
                .
            </Font.P>
        ),
        include: "CodeContainer",
    },
    {
        title: "header",
        intro: "The header has to be placed outside the container.",
        include: "Header",
    },
    {
        title: "footer",
        intro: "The footer can be useful to show external links, sending to partners' websites for example, or legal links.",
        include: "",
    },

    // Helpers
    {
        title: "variables",
        intro: "To speed up the development process Fajny React uses a lot of variables.",
        include: "Variables",
    },
    {
        title: "mixins",
        intro: "To speed up your development process with Styled Components you can use mixins. These are really useful shortcodes to add in your components.",
        include: "Mixins",
    },
    {
        title: "helpers",
        intro: "Helpers are classes created to override some CSS properties in your React code. Just add them in the className of your element.",
    },
    {
        title: "js-utils",
        intro: "To be able to use these functions in your project, you need to install this package separately from Fajny React:",
    },
    {
        title: "sr-only",
        intro: "Hide elements on every devices except screen readers. You can also use it as a CSS helper class.",
        include: "SrOnly",
    },
]

export default allComponentsPages
